
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * ConfigHistory struct
 */
class ConfigHistory {
  /**
   * Initialize struct
   * @param {!string=} historyId
   * @param {!Date=} timestamp
   * @param {!string=} comment
   * @constructor
   */
  constructor (historyId = '', timestamp = new Date(0), comment = '') {
    this.HistoryId = historyId
    this.Timestamp = timestamp
    this.Comment = comment
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ConfigHistory}
   * @param {!ConfigHistory} other Other struct
   * @returns {!ConfigHistory} This struct
   */
  copy (other) {
    if (other.HistoryId != null) {
      this.HistoryId = other.HistoryId
    } else {
      this.HistoryId = null
    }
    if (other.Timestamp != null) {
      if (other.Timestamp instanceof Date) {
        this.Timestamp = new Date(other.Timestamp.getTime())
      } else {
        this.Timestamp = new Date(Math.round(other.Timestamp / 1000000))
      }
    } else {
      this.Timestamp = null
    }
    if (other.Comment != null) {
      this.Comment = other.Comment
    } else {
      this.Comment = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ConfigHistory}
   * @returns {!ConfigHistory} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ConfigHistoryModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ConfigHistoryModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ConfigHistory}
   * @param {!ConfigHistory} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ConfigHistory)) {
      throw new TypeError('Instance of ConfigHistory is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ConfigHistory}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      HistoryId: ((this.HistoryId != null) ? this.HistoryId : null),
      Timestamp: ((this.Timestamp != null) ? (this.Timestamp.getTime() * 1000000) : null),
      Comment: ((this.Comment != null) ? this.Comment : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ConfigHistory.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ConfigHistory} other Object value
   * @returns {!ConfigHistory} Created struct
   */
  static fromObject (other) {
    return new ConfigHistory().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ConfigHistory}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ConfigHistory.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ConfigHistory}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 1021
  }
}

export { ConfigHistory };

/**
 * Fast Binary Encoding ConfigHistory field model
 */
class FieldModelConfigHistory extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.HistoryId = new fbe.FieldModelString(buffer, 4 + 4)
    this.Timestamp = new fbe.FieldModelTimestamp(buffer, this.HistoryId.fbeOffset + this.HistoryId.fbeSize)
    this.Comment = new fbe.FieldModelString(buffer, this.Timestamp.fbeOffset + this.Timestamp.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelConfigHistory}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelConfigHistory}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.HistoryId.fbeSize + this.Timestamp.fbeSize + this.Comment.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelConfigHistory}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.HistoryId.fbeExtra + this.Timestamp.fbeExtra + this.Comment.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelConfigHistory}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelConfigHistory.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelConfigHistory}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 1021
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelConfigHistory}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelConfigHistory}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.HistoryId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.HistoryId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.HistoryId.fbeSize

    if ((fbeCurrentSize + this.Timestamp.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Timestamp.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Timestamp.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Comment.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelConfigHistory}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelConfigHistory}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelConfigHistory}
   * @param {!ConfigHistory} fbeValue Default value, defaults is new ConfigHistory()
   * @returns {!ConfigHistory} ConfigHistory value
   */
  get (fbeValue = new ConfigHistory()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelConfigHistory}
   * @param {!ConfigHistory} fbeValue ConfigHistory value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.HistoryId.fbeSize) <= fbeStructSize) {
      fbeValue.HistoryId = this.HistoryId.get()
    } else {
      fbeValue.HistoryId = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.HistoryId.fbeSize

    if ((fbeCurrentSize + this.Timestamp.fbeSize) <= fbeStructSize) {
      fbeValue.Timestamp = this.Timestamp.get()
    } else {
      fbeValue.Timestamp = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Timestamp.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) <= fbeStructSize) {
      fbeValue.Comment = this.Comment.get()
    } else {
      fbeValue.Comment = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelConfigHistory}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelConfigHistory}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelConfigHistory}
   * @param {!ConfigHistory} fbeValue ConfigHistory value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelConfigHistory}
   * @param {ConfigHistory} fbeValue ConfigHistory value
   */
  setFields (fbeValue) {
    this.HistoryId.set(fbeValue.HistoryId)
    this.Timestamp.set(fbeValue.Timestamp)
    this.Comment.set(fbeValue.Comment)
  }
}

export { FieldModelConfigHistory };

/**
 * Fast Binary Encoding ConfigHistory model
 */
class ConfigHistoryModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelConfigHistory(this.buffer, 4)
  }

  /**
   * Get the ConfigHistory model
   * @this {!ConfigHistoryModel}
   * @returns {!FieldModelConfigHistory} model ConfigHistory model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ConfigHistoryModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ConfigHistoryModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ConfigHistoryModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ConfigHistoryModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelConfigHistory.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ConfigHistoryModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ConfigHistoryModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ConfigHistoryModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ConfigHistoryModel}
   * @param {!ConfigHistory} value ConfigHistory value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ConfigHistoryModel}
   * @param {!ConfigHistory} value ConfigHistory value, defaults is new ConfigHistory()
   * @return {!object} Deserialized ConfigHistory value and its size
   */
  deserialize (value = new ConfigHistory()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ConfigHistory(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ConfigHistory(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ConfigHistoryModel}
   * @param {!number} prev Previous ConfigHistory model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ConfigHistoryModel };
